import { create } from 'zustand';

// PERFORM OR MINGLE, the client half (API sockets/venueMingle, 2026-10-30).
//
// Khari: *"the 'perform or mingle' choice — mingle when the user is the only
// user. This can mimic the radio DJ logic."* The SERVER decides, from who is
// standing in the room (the /world socket's own presence), and tells the room:
//
//   venue_mode  { place, a, t0, mode: 'mingle' | 'perform' | null, since, t }
//               null = the set is over (or another took the room)
//   venue_dj    { place, a, t0, mode, kind, transcript, audioUrl | null, host: { avatarId, name }, ts }
//               one of the host's lines: the radio DJ's solo brain (RoomDjDirector)
//               talking to the one person in the room. audioUrl null = a caption only
//               (a guest's answers are not voiced).
//
//   venue_dj_cue { place, a, t0, mode, kind, text, ts }
//               AGENT MODE (2026-10-31): the host's own voice agent is open here
//               (components/VenueAgent), so a moment comes as a stage direction for
//               it instead of a recorded line; VenueAgent hands it over. To this
//               socket only.
//
// VenueStage reads both: where the host stands (the booth, or beside you), how
// loud the set is (under a conversation, and lower again while they speak), and
// each line in their voice with its caption. Nothing here decides anything.
export const useVenueMingle = create((set) => ({
  modes: {},      // place → { a, t0, mode, since }
  line: null,     // the newest line: { id, place, a, t0, mode, kind, text, audioUrl, host, at, agent? }
  cue: null,      // the newest stage direction for the host's agent: { id, place, a, kind, text, at }
  reset: () => set({ modes: {}, line: null, cue: null }),
}));

let sock = null;
let seq = 0;

/** Called by worldSocket for each socket it makes (a reconnect builds a new one). */
export function bindVenueMingle(socket) {
  sock = socket;
  socket.on('venue_mode', (m = {}) => {
    if (!m?.place) return;
    const modes = { ...useVenueMingle.getState().modes };
    if (!m.mode) delete modes[m.place];
    else modes[m.place] = { a: m.a, t0: m.t0, mode: m.mode, since: m.since || null };
    useVenueMingle.setState({ modes });
  });
  socket.on('venue_dj', (m = {}) => {
    if (!m?.place || !m.transcript) return;
    useVenueMingle.setState({
      line: {
        id: ++seq, place: m.place, a: m.a, t0: m.t0, mode: m.mode || null, kind: m.kind || null,
        text: String(m.transcript), audioUrl: m.audioUrl || null, host: m.host || null, at: Date.now(),
      },
    });
  });
  socket.on('venue_dj_cue', (m = {}) => {
    if (!m?.place || !m.text) return;
    useVenueMingle.setState({ cue: { id: ++seq, place: m.place, a: m.a, kind: m.kind || null, text: String(m.text), at: Date.now() } });
  });
  // A dropped socket knows nothing: back to "performing" until the room says otherwise
  // (`enter` on the reconnect brings the room's state back).
  socket.on('disconnect', () => useVenueMingle.getState().reset());
}

/** A line the host's agent said (VenueAgent): played by VenueStage like any of theirs, its audio already streaming. */
export function pushAgentLine({ place, a, name, text }) {
  if (!place || !text) return;
  useVenueMingle.setState({
    line: { id: ++seq, place, a, t0: null, mode: 'mingle', kind: 'agent', text: String(text), audioUrl: null, host: { avatarId: a, name }, at: Date.now(), agent: true },
  });
}

/** Tell the server about the host's agent here: { place, state } | { place, said } (API venueMingle.agent). */
export function venueAgentSignal(payload) {
  if (sock?.connected && payload?.place) sock.emit('venue_agent', payload);
}

/** Ask where the set in this room stands (VenueStage on mount). */
export function venueHello(place) {
  if (sock?.connected && place) sock.emit('venue_hello', { place });
}

if (import.meta.env?.DEV && typeof window !== 'undefined') window.__venueMingle = useVenueMingle;
